import type React from 'react';
import type { LucideIcon } from 'lucide-react';
import { CostUnit } from '../types/Enums';
import { translate } from '../types/EnumTranslations';
import { RELEVANT_BASE_TYPES } from '../Helpers/baseTypeUtils';
import { LibraryFormData, LibraryTemplate } from '../types/LibraryTemplate';

/**
 * Everything that differs between two superadmin libraries.
 *
 * The generic LibraryApi, useLibrary hook, LibraryPage and LibraryPickerModal
 * are driven entirely by one of these objects, so adding a library means
 * adding a config file — see maintenanceLibrary.tsx and lifetimeLibrary.tsx.
 *
 * `F` is the library's own field set (everything except name, base types
 * and metadata, which are shared).
 */

export type CsvColumnsResult<F extends object> =
    | { ok: true; fields: F }
    | { ok: false; label: string; detail: string };

export interface LibraryLabels {
    /** Page heading, e.g. "Underhållsbibliotek" */
    title: string;
    /** Lead paragraph under the heading */
    description: string;
    icon: LucideIcon;
    /** Hook/picker error when the list cannot be loaded */
    loadError: string;
    /** Picker modal heading */
    pickerTitle: string;
    /** Placeholder for the name input in the add modal */
    addNamePlaceholder: string;
    /** Reassurance shown in the delete confirmation */
    deleteNote: string;
    /** Definite noun used in the reset dialog, e.g. "underhållsbiblioteket" */
    resetNoun: string;
    /** Help text in the CSV import modal (the column list) */
    csvHelp: string;
    /** File name prefix for exports, e.g. "underhallsbibliotek" */
    exportFilePrefix: string;
}

/**
 * How the superadmin list ("grouped" view) and the picker group templates.
 * A template may sit in several groups (base types) or exactly one (a category).
 */
export interface LibraryGrouping<F extends object> {
    /** View-mode option text, e.g. "Gruppera efter bastyp" */
    viewLabel: string;
    /** Groups in display order */
    groups: ReadonlyArray<{ key: string; label: string }>;
    /** Group keys the template belongs to */
    groupsOf(template: LibraryTemplate<F>): string[];
}

export interface LibraryConfig<F extends object> {
    /**
     * Whether templates carry base types (default true): editor field,
     * "Bastyper" CSV column and the picker's "Föreslagna" split. A library
     * without them stores none (Firebase drops the empty array).
     */
    usesBaseTypes?: boolean;
    /** Grouping for the list and picker. Defaults to base types. */
    grouping?: LibraryGrouping<F>;
    /** Firebase path of the library node, e.g. config/maintenanceLibraryTemplates */
    path: string;
    /** Keys of F. Used to pick the specific fields out of forms and templates for writes. */
    fieldKeys: ReadonlyArray<keyof F>;
    /** Initial values for the add form */
    defaultFields: F;
    /**
     * Read the library-specific fields out of a raw Firebase node.
     * Return null (after a console.warn) to skip a malformed record.
     */
    parseFields(data: Record<string, unknown>, key: string): F | null;
    /**
     * Throw an Error with a Swedish message if the fields are invalid.
     * 'full' = all fields required (create/import), 'partial' = only check
     * the fields that are present (update).
     */
    validateFields(fields: Partial<F>, mode: 'full' | 'partial'): void;
    labels: LibraryLabels;
    csv: {
        /** Column headers after "Namn" (and before "Bastyper" when the library uses base types) */
        columns: string[];
        /** Parse the cells for `columns` (already trimmed) into fields */
        parseColumns(cells: string[]): CsvColumnsResult<F>;
        /** Format a template's fields as the cells for `columns` (unescaped) */
        formatColumns(template: LibraryTemplate<F>): string[];
    };
    /** Render the inputs for the specific fields (used by both add modal and editor) */
    renderFields(
        form: LibraryFormData<F>,
        setForm: React.Dispatch<React.SetStateAction<LibraryFormData<F>>>,
        disabled: boolean
    ): React.ReactNode;
    /** One-line summary for picker rows, e.g. "5 år · 500 kr/m²" */
    formatSummary(template: LibraryTemplate<F>): string;
}

// ============================================================
// Helpers shared by library configs
// ============================================================

export const ALL_COST_UNITS: ReadonlyArray<CostUnit> = ['per_sqm', 'per_unit', 'per_lm'];

export const COST_UNIT_OPTIONS: ReadonlyArray<{ value: CostUnit; label: string }> = [
    { value: 'per_sqm', label: 'kr/m²' },
    { value: 'per_unit', label: 'kr/st' },
    { value: 'per_lm', label: 'kr/lm' },
];

export function isCostUnit(value: unknown): value is CostUnit {
    return typeof value === 'string' && (ALL_COST_UNITS as ReadonlyArray<string>).includes(value);
}

/**
 * Copy the given keys (skipping undefined) out of a form or template.
 * Returns Partial<F> because a partial update may carry only some keys.
 */
export function pickFields<F extends object>(source: object, keys: ReadonlyArray<keyof F>): Partial<F> {
    const src = source as Record<PropertyKey, unknown>;
    const out: Record<PropertyKey, unknown> = {};
    for (const key of keys) {
        const value = src[key as PropertyKey];
        if (value !== undefined) {
            out[key as PropertyKey] = value;
        }
    }
    return out as Partial<F>;
}

/** The library's grouping: its own, or one group per relevant base type. */
export function libraryGrouping<F extends object>(config: LibraryConfig<F>): LibraryGrouping<F> {
    return (
        config.grouping ?? {
            viewLabel: 'Gruppera efter bastyp',
            groups: RELEVANT_BASE_TYPES.map((baseType) => ({ key: baseType, label: translate(baseType) })),
            groupsOf: (template) => template.baseTypes,
        }
    );
}

export function usesBaseTypes<F extends object>(config: Pick<LibraryConfig<F>, 'usesBaseTypes'>): boolean {
    return config.usesBaseTypes !== false;
}
